/* Atlas v3 — the Overview screen (page === 'home' in the mockup).
   Header · Pulse · MarketsDoor · Brief · WireTeaser · PickOfWeek,
   with the shared shell (market tape + bottom nav). Brings its own shell —
   NOT wrapped in the old AppLayout.

   Data: real hooks where the mapping is 1:1 (Brief ← useAtlasIntel,
   WireTeaser ← useSignalsToday, PickOfWeek ← usePickOfWeek). Visual-only
   seed data is kept with a TODO(data) where no real source maps in one
   step (the smooth index SERIES for the starfield clip, the sector
   marquee). Visual fidelity wins per the brief. */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import {
  CfgCtx, TickerCtx, DEFAULT_CFG, useCfg, useTicker,
  Kicker, Section, SecHead, Chip, Pct,
  marketState, intent, REDUCE, signalToSheet,
} from './lib'
import { HeroChart } from './HeroChart'
import { SectorLines, sectorHeadline, toggleSector } from './SectorLines'
import { PickSheet } from './PickSheet'
import { NewsSheet } from './NewsSheet'
import { BriefSheet } from './BriefSheet'
import { TickerStrip, BottomNav, SearchPill } from './shell'
import { StockLogo, LOGO_MISSING } from '@/v2/components/StockLogo'
import { StockDetailProvider, useStockDetail } from '@/v2/lib/StockDetailProvider'
import { useAtlasIntel, usePickOfWeek, useNews, useAtlasIndex, useSectorIndex } from '@/v2/hooks/useSupabaseData'
import { useAtlasStocks } from '@/v2/hooks/useAtlasStocks'
import { useLivePrices } from '@/v2/hooks/useLivePrices'
import { useChartBars } from '@/v2/hooks/useChartBars'
import { usePortfolioTickers } from '@/v2/hooks/usePortfolioTickers'
import { useWatchlists } from '@/v2/hooks/useWatchlists'
import { useUserPref } from '@/v2/hooks/useUserPref'
import { v2Navigate } from '@/v2/lib/useV2Path'


/* ── header ───────────────────────────────────────────────── */
// Search is the shared <SearchPill /> from shell.jsx, sitting immediately left
// of the "B" avatar exactly as the design reference has it. Every other v3
// screen puts the same pill in the top-right of its own page header, so search
// is reachable everywhere without a per-screen implementation.
function Header() {
  const [ms, setMs] = useState(() => marketState())
  useEffect(() => { const id = setInterval(() => setMs(marketState()), 30000); return () => clearInterval(id) }, [])
  const dotColor = `hsl(var(${ms.dot}))`
  return (
    <div style={{ padding: '8px 20px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontFamily: 'var(--studio-font-display)', fontStyle: 'italic', fontSize: 30, lineHeight: 1, color: 'hsl(var(--studio-accent-soft))' }}>Atlas</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: dotColor, flexShrink: 0, animation: ms.open ? 'atlasBloom 2.4s infinite' : 'none', display: 'inline-block' }} />
          <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 10, color: 'hsl(var(--studio-text-subtle))', letterSpacing: '0.04em' }}>{new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).replace(/,/g, '').toUpperCase()}</span>
        </div>
        <SearchPill />
        <div data-header-avatar style={{ width: 34, height: 34, borderRadius: '50%', border: '1px solid hsl(var(--studio-border))', background: 'hsl(var(--studio-surface-2))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--studio-font-mono)', fontSize: 13, color: 'hsl(var(--studio-accent-soft))' }}>B</div>
      </div>
    </div>
  )
}

/* ── 1. The Pulse (hero) ───────────────────────────────────────
   The v3 Observatory chart (matches the design mockup): warm-green area,
   amber-underline 1D/1W/1M/6M/1Y chips, headline %. The Atlas Index source
   reads the whole-main-tier atlas_index (~266 names, daily) for the % + line;
   the other sources (Portfolio / Watchlists / Sectors) use a live chart-bars
   composite of their ~40-name basket. Replaces the V2 FilterableChart, whose
   sector dropdown + neon green + gridded axes clashed with the v3
   palette/type. (The Observatory starfield that once clipped to this chart
   was deleted 2026-08-15 after two months parked; the data-pulse-* tags
   remain as harmless anchors.) ── */

const PULSE_TFS = [['1D', '1D'], ['1W', '5D'], ['1M', '1M'], ['6M', '6M'], ['1Y', '1Y']]
// Same vocabulary as the index chips wherever the data supports it — 1M/6M/1Y
// are the identical labels and windows — then extended with 5Y/MAX. It does NOT
// offer 1D/1W because `sector_index` stores ONE row per sector per trading day:
// 1D would be a single point and 1W five. A separate 1Y/3Y/5Y/MAX vocabulary
// was the wrong answer to that (Ben: "why wouldnt it just match the existing
// chart"); matching where it can and stopping where the data stops is honest.
// Third field is the log flag — past 1Y the sectors spread over orders of
// magnitude and a linear axis crushes all but the leader onto the baseline.
const SECTOR_TFS = [['1M', 30, false], ['6M', 182, false], ['1Y', 365, false], ['5Y', 1825, true], ['MAX', 0, true]]
function Pulse({ sources, idxLevels, liveIndexPct }) {
  // Settings → Display → "Live pulse" — desktop already honors it; phone didn't
  const [livePulse] = useUserPref('live_pulse_enabled', true)
  const [tf, setTf] = useState('1D')
  // Hero mode: the single Atlas index line (default — it's the glanceable
  // headline) or the 9-sector comparison. Lazy: the sector series is only
  // fetched once the mode is actually switched on.
  const [mode, setMode] = useState('index')
  const [sectorTf, setSectorTf] = useState('1Y')
  const sectorCfg = SECTOR_TFS.find(([l]) => l === sectorTf) || SECTOR_TFS[0]
  const sectorDays = sectorCfg[1]
  const sectorLog = sectorCfg[2]
  const { series: sectorSeries, loading: sectorLoading } = useSectorIndex(mode === 'sectors', sectorDays)
  // Which sectors the comparison draws. Empty = all of them (the survey
  // default). Persisted per user, so "Memory vs Semi Equip" survives a reload
  // and follows you between phone and desktop — the pair you are watching this
  // week is exactly the kind of state that is infuriating to re-pick.
  const [sectorSel, setSectorSel] = useUserPref('hero_sector_selection', [])
  const toggleSel = (id) => setSectorSel(toggleSector(sectorSel, id))
  const resetSel = () => setSectorSel([])
  const sectorHead = sectorHeadline(sectorSeries, sectorSel)
  const [srcKey, setSrcKey] = useState('atlas')
  const [menuOpen, setMenuOpen] = useState(false)
  const [hover, setHover] = useState(null)  // { value, time } | null — scrub state
  const range = (PULSE_TFS.find(([l]) => l === tf) || PULSE_TFS[0])[1]
  const isAtlas = srcKey === 'atlas'
  // Atlas Index source, two modes:
  //  · 1D  = LIVE whole-index move on the day → headline = live equal-weight avg
  //    change1d across the main tier (liveIndexPct); the LINE is the live intraday
  //    composite, endpoint-anchored to liveIndexPct so it lands on the headline.
  //  · >1D = the daily atlas_index series (true index over time; real curves).
  // Non-atlas sources stay live chart-bars composites.
  // Atlas index render modes:
  //  · 1D + 1W (atlasIntraday) → the DENSE intraday composite line (hourly
  //    chart-bars of the basket), anchored so its endpoint = the true index %
  //    for the range. The daily atlas_index only has ONE level/day, so 1W as a
  //    daily series was just ~6 lonely points; the composite gives ~5 days of
  //    hourly detail while the headline stays the real index move.
  //  · 1M+ (atlasDaily) → the true daily atlas_index series (already 22/126/252
  //    points — plenty dense, and the honest long-run curve).
  const atlasIntraday = isAtlas && (range === '1D' || range === '5D')
  const atlasDaily = isAtlas && !atlasIntraday
  const atlasLive = isAtlas && range === '1D'
  const cur = useMemo(() => sources.find((s) => s.key === srcKey) || sources[0] || { key: 'atlas', label: 'Atlas Index', tickers: [] }, [sources, srcKey])
  const intraday = !atlasDaily && (tf === '1D' || tf === '1W')
  const basket = useMemo(() => (atlasDaily ? [] : (cur.tickers || []).slice(0, 40)), [cur, atlasDaily])
  const { series } = useChartBars(basket, range)
  // Whole-index window from atlas_index daily levels, normalized to % from the
  // window start. SPAN = trading days per timeframe. Computed for any atlas
  // range: it's the series for 1M+, and the true-endpoint anchor for 1W.
  const idxWindow = useMemo(() => {
    if (!isAtlas || !idxLevels?.length) return null
    const SPAN = { '5D': 6, '1M': 22, '6M': 126, '1Y': 252 }[range] || idxLevels.length
    const win = idxLevels.slice(-SPAN)
    if (win.length < 2) return { points: [], pct: 0 }
    const base = win[0].level
    const pts = win.map((l) => ({ t: new Date(l.date + 'T00:00:00').getTime(), value: (l.level / base - 1) * 100 }))
    return { points: pts, pct: pts[pts.length - 1].value }
  }, [isAtlas, idxLevels, range])
  const composLast = series.length ? series[series.length - 1].pct : 0
  // Anchor the intraday composite's endpoint to the true index % for the range:
  // 1D → the live whole-index move; 1W → the true 5-day index move
  // (idxWindow.pct). Keeps the dense intraday shape but lands on the real number.
  const anchorPct = atlasLive ? liveIndexPct : (atlasIntraday ? idxWindow?.pct : null)
  const atlasDelta = atlasIntraday && anchorPct != null ? anchorPct - composLast : 0
  const composPoints = useMemo(() => series.map((p) => ({ t: p.t, value: p.pct + atlasDelta })), [series, atlasDelta])
  const points = atlasDaily ? (idxWindow?.points || []) : composPoints
  const lastPct = atlasDaily ? (idxWindow?.pct || 0) : (atlasIntraday ? (anchorPct ?? composLast) : composLast)
  // The LINE + heartbeat color reflect the period direction (lastPct); only the
  // headline NUMBER follows the scrub (hovered point), so the chart doesn't
  // recolor green↔red as you drag across zero.
  const scrub = hover && hover.value != null
  const lineColor = intent(lastPct >= 0)
  const shownPct = scrub ? hover.value : lastPct
  const numColor = intent(shownPct >= 0)
  // Label under the % = the hovered timestamp while scrubbing, else the tf.
  // Intraday points (1D + 1W composite) → date + time; daily points → date.
  // DEVICE-LOCAL time (no timeZone pin) — each viewer sees their own clock,
  // 12-hour. ET stays only in market-hours *logic* (lib/Wire/Calendar).
  const tfLabel = scrub
    ? new Date(hover.time * 1000).toLocaleString('en-US', intraday
      ? { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }
      : { month: 'short', day: 'numeric' })
    : tf
  const GROUP_LABEL = { world: 'Overnight', portfolio: 'Yours', watchlist: 'Watchlists', sector: 'Sectors' }

  return (
    <Section style={{ marginBottom: 26, paddingTop: 4 }}>
      {/* Source toggle — compact label + caret; menu groups Index / Yours /
          Watchlists / Sectors (Portfolio + Watchlists wired back in). */}
      <div style={{ position: 'relative', marginBottom: 5 }}>
        <button onClick={() => setMenuOpen((v) => !v)} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '82%', background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          fontFamily: 'var(--studio-font-mono)', fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase',
          color: srcKey === 'atlas' ? 'hsl(var(--studio-text-subtle))' : 'hsl(var(--studio-accent-soft))',
        }}>
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cur.label}</span>
          <span style={{ fontSize: 8, opacity: 0.7, flexShrink: 0 }}>▾</span>
        </button>
        {menuOpen && (
          <>
            <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
            <div className="atlas-scroll" style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 41, minWidth: 220, maxHeight: 320, overflowY: 'auto', background: 'hsl(var(--studio-surface))', border: '1px solid hsl(var(--studio-border))', borderRadius: 8, padding: 4, boxShadow: '0 12px 32px rgba(0,0,0,0.5)' }}>
              {sources.map((s, i) => {
                const on = s.key === srcKey
                const head = GROUP_LABEL[s.group] && (i === 0 || sources[i - 1].group !== s.group) ? GROUP_LABEL[s.group] : null
                return (
                  <div key={s.key}>
                    {head && <div style={{ padding: '8px 10px 3px', fontFamily: 'var(--studio-font-mono)', fontSize: 8.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))' }}>{head.toUpperCase()}</div>}
                    <button onClick={() => { setSrcKey(s.key); setHover(null); setMenuOpen(false) }} style={{
                      display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: 5, border: 'none', cursor: 'pointer',
                      background: on ? 'hsl(var(--studio-surface-2))' : 'transparent',
                      color: on ? 'hsl(var(--studio-text))' : 'hsl(var(--studio-text-muted))',
                      fontFamily: 'var(--studio-font-sans)', fontSize: 12.5,
                    }}>{s.label}</button>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
      {/* Headline % (scrubs) + period label.
          In SECTORS mode there is no single number to headline — the chart is a
          comparison of nine series, each carrying its own % in the legend. It
          used to keep showing the Atlas index figure and its own timeframe
          ("+1.45% 1D") above a MAX sector chart, which contradicted everything
          under it. Show what the view actually is instead. */}
      <div data-pulse-num="1" style={{ display: 'flex', alignItems: 'flex-end', gap: 11, margin: '0 0 2px' }}>
        {mode === 'sectors' ? (
          <>
            {/* Step the display size down for a named pair — "Memory vs Semi
                Equip" at 28px italic serif overruns a 390px phone. */}
            <span style={{ fontFamily: 'var(--studio-font-display)', fontStyle: 'italic', fontSize: sectorHead.length > 24 ? 16 : sectorHead.length > 14 ? 20 : 28, lineHeight: 0.95, color: 'hsl(var(--studio-text))' }}>
              {sectorHead}
            </span>
            <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 9.5, letterSpacing: '0.12em', color: 'hsl(var(--studio-text-subtle))', paddingBottom: 6, alignSelf: 'flex-end' }}>
              {sectorTf}{sectorLog ? ' · LOG' : ''}
            </span>
          </>
        ) : (
          <>
        <Pct value={shownPct} animate={!scrub} style={{ fontFamily: 'var(--studio-font-display)', fontStyle: 'italic', fontSize: 28, lineHeight: 0.95, color: `hsl(var(${numColor}))` }} />
        <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 9.5, letterSpacing: '0.12em', color: 'hsl(var(--studio-text-subtle))', paddingBottom: 6, alignSelf: 'flex-end' }}>{tfLabel}</span>
          </>
        )}
      </div>
      <div data-pulse-chart="1" style={{ margin: '18px -20px 4px' }}>
        {mode === 'sectors'
          ? (sectorLoading && sectorSeries.length === 0
              ? <div className="atlas-skeleton" style={{ height: 168, borderRadius: 0 }} />
              : <div style={{ padding: '0 20px' }}><SectorLines series={sectorSeries} height={168} days={sectorDays} log={sectorLog}
                  selected={sectorSel} onToggle={toggleSel} onReset={resetSel} /></div>)
          : points.length < 2
            ? <div className="atlas-skeleton" style={{ height: 168, borderRadius: 0 }} /> /* cold load / failed fetch — desktop shows SkelChart here too; a blank canvas read as broken */
            : <HeroChart points={points} color={lineColor} height={168} intraday={intraday} onHover={setHover} pulse={!!livePulse} />}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
        {/* Each mode gets its own chip set. The sector index is daily-only —
            there is no intraday sector series — so it offers 1Y/3Y/5Y/MAX
            rather than the index line's 1D/1W/1M/6M/1Y. */}
        {mode === 'index'
          ? PULSE_TFS.map(([l]) => <Chip key={l} active={tf === l} onClick={() => setTf(l)}>{l}</Chip>)
          : SECTOR_TFS.map(([l]) => <Chip key={l} active={sectorTf === l} onClick={() => setSectorTf(l)}>{l}</Chip>)}
        <button
          onClick={() => setMode(mode === 'sectors' ? 'index' : 'sectors')}
          aria-pressed={mode === 'sectors'}
          style={{ all: 'unset', cursor: 'pointer', marginLeft: 'auto', padding: '3px 8px',
            borderRadius: 999, border: `1px solid hsl(var(--studio-${mode === 'sectors' ? 'accent' : 'border'}))`,
            fontFamily: 'var(--studio-font-mono)', fontSize: 8.5, letterSpacing: '0.1em',
            color: `hsl(var(--studio-${mode === 'sectors' ? 'accent' : 'text-subtle'}))` }}>
          SECTORS
        </button>
      </div>
    </Section>
  )
}

/* ── 2. Markets door — Explore entry. Borderless per the v3 handoff: a
   top/bottom hairline band on the black base (NOT a rounded card), logo
   cluster + "+N NAMES" + best/worst sector readout. ── */
const DOOR_LOGOS = ['NVDA', 'AVGO', 'AMD', 'TSM', 'ASML', 'AMAT', 'MU', 'PLTR', 'OKLO', 'KTOS', 'MRVL', 'CRWV']

function MarketsDoor({ onOpen, pool }) {
  const [hot, setHot] = useState(false)
  const [valid, setValid] = useState([]) // tickers whose logo actually loaded
  const validRef = useRef([])            // accumulates across probe runs (never shrinks)
  const openTicker = useTicker()
  const trackRef = useRef(null)
  const offsetRef = useRef(0)     // px scrolled, kept in [0, setWidth)
  const setWidthRef = useRef(0)   // width of one logo set (half the doubled track)
  const dragRef = useRef(null)    // { startX, startOffset, moved } while a finger is down
  const movedRef = useRef(false)  // suppress the click that follows a drag

  // Candidate tickers (market-cap order from the universe), deduped. Keyed by
  // content so price-poll refreshes that return the same names don't re-run the
  // probe (which would blank the strip). We pull a big slice so the strip shows
  // lots of the universe, not just a handful.
  const candidatesKey = useMemo(() => {
    const base = (pool && pool.length ? pool : DOOR_LOGOS)
    return [...new Set(base)].slice(0, 160).join(',')
  }, [pool])

  // Pre-validate logos: probe each candidate's /api/stock-logo with an Image and
  // keep only the ones that load — only names with a REAL logo ever render (no
  // ticker-letter fallbacks scroll), and they're warm in cache so they don't
  // flash in. Accumulated in a ref so the list only grows (no blank when pool
  // swaps from the seed to the live universe, no re-probe churn on refreshes).
  useEffect(() => {
    const candidates = candidatesKey ? candidatesKey.split(',') : []
    if (!candidates.length) return
    let cancelled = false
    const NEED = 64        // plenty of variety before the loop repeats
    const CONCURRENCY = 6
    let i = 0
    const probeNext = () => {
      if (cancelled) return
      if (validRef.current.length >= NEED || i >= candidates.length) return
      const t = candidates[i++]
      if (validRef.current.includes(t)) { probeNext(); return } // already kept
      if (LOGO_MISSING.has(t)) { probeNext(); return }          // known miss
      const img = new Image()
      img.onload = () => {
        if (cancelled) return
        if (!validRef.current.includes(t)) {
          validRef.current = [...validRef.current, t]
          setValid(validRef.current)
        }
        probeNext()
      }
      img.onerror = () => { LOGO_MISSING.add(t); if (!cancelled) probeNext() }
      img.src = `/api/stock-logo?ticker=${t}`
    }
    for (let k = 0; k < CONCURRENCY; k++) probeNext()
    return () => { cancelled = true }
  }, [candidatesKey])

  const track = [...valid, ...valid]

  // Re-measure one set's width after the logo list changes (runs post-paint, so
  // scrollWidth is real). Cached so the rAF never touches layout.
  useEffect(() => {
    if (trackRef.current) setWidthRef.current = trackRef.current.scrollWidth / 2
  }, [valid.length])

  // Auto-drift via a GPU transform, NOT scrollLeft. translateX is a compositor-
  // only property — updating it per frame avoids the layout/paint churn that made
  // the scrollLeft version lag, and it stays smooth no matter how many logos load.
  // Drag and auto-drift share one offset, so there's no scroll/transform fighting.
  useEffect(() => {
    if (REDUCE) return
    let raf
    const SPEED = 0.4 // px/frame ≈ 24px/s
    const tick = () => {
      if (!dragRef.current) {
        const sw = setWidthRef.current
        if (sw > 0) {
          let o = offsetRef.current + SPEED
          if (o >= sw) o -= sw
          offsetRef.current = o
          if (trackRef.current) trackRef.current.style.transform = `translateX(${-o}px)`
        }
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [valid.length])

  function applyOffset(o) {
    const sw = setWidthRef.current || 1
    const wrapped = ((o % sw) + sw) % sw // keep in [0, setWidth)
    offsetRef.current = wrapped
    if (trackRef.current) trackRef.current.style.transform = `translateX(${-wrapped}px)`
  }
  function onPointerDown(e) {
    dragRef.current = { startX: e.clientX, startOffset: offsetRef.current }
    movedRef.current = false
    setHot(true)
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }
  function onPointerMove(e) {
    const d = dragRef.current
    if (!d) return
    const delta = e.clientX - d.startX
    if (Math.abs(delta) > 3) movedRef.current = true
    applyOffset(d.startOffset - delta) // drag right → content follows the finger
  }
  function onPointerUp() { dragRef.current = null; setHot(false) }

  // Tap (no drag) → hit-test the logos by their live rect (transform-accurate)
  // and open that ticker; a drag suppresses the click; a tap on empty strip
  // falls through to the door's onOpen → Explore.
  function onClickStrip(e) {
    if (movedRef.current) { movedRef.current = false; e.stopPropagation(); return }
    const track = trackRef.current
    if (!track) return
    for (const el of track.children) {
      const r = el.getBoundingClientRect()
      if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
        e.stopPropagation()
        if (el.dataset.ticker) openTicker(el.dataset.ticker)
        return
      }
    }
  }

  return (
    <Section style={{ padding: 0 }}>
      <div onClick={onOpen}
        style={{ cursor: 'pointer', borderTop: '1px solid hsl(var(--studio-border))', borderBottom: '1px solid hsl(var(--studio-border))', padding: '16px 0 18px', background: hot ? 'hsl(var(--studio-surface) / 0.4)' : 'transparent', transition: 'background 140ms var(--studio-ease)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12, padding: '0 20px' }}>
          <div>
            <div style={{ fontFamily: 'var(--studio-font-display)', fontStyle: 'italic', fontSize: 20, color: 'hsl(var(--studio-text))' }}>Explore the markets</div>
          </div>
          <span style={{ color: 'hsl(var(--studio-accent-soft))', fontSize: 20, lineHeight: 1 }}>→</span>
        </div>
        {/* Transform-driven marquee: drag to scroll, tap a logo to open its ticker,
            auto-drifts when idle. touch-action pan-y lets vertical page scroll
            through while horizontal drags move the strip. */}
        <div
          onClick={onClickStrip}
          onPointerDown={onPointerDown} onPointerMove={onPointerMove}
          onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
          style={{ overflow: 'hidden', width: '100%', touchAction: 'pan-y', cursor: 'grab',
            maskImage: 'linear-gradient(90deg, transparent, #000 5%, #000 95%, transparent)',
            WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 5%, #000 95%, transparent)' }}>
          <div ref={trackRef} style={{ display: 'flex', alignItems: 'center', width: 'max-content', minHeight: 40, willChange: 'transform' }}>
            {track.map((t, i) => (
              <div key={i} data-ticker={t} aria-label={t}
                style={{ marginLeft: -10, border: '2px solid hsl(var(--studio-background))', borderRadius: '50%', display: 'flex', background: 'hsl(var(--studio-background))', lineHeight: 0, flexShrink: 0, padding: 0 }}>
                <StockLogo ticker={t} size={36} rounded="50%" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  )
}

/* ── 3. The Brief ─────────────────────────────────────────── */
// First editorial paragraph of the intel body, markdown stripped (skips the
// "## Market Pulse" heading + the "Atlas Intel —" title line).
// Tells that the intel writer described its own (thin/empty) input instead of
// writing a brief — e.g. "There are no percentage figures … in the SECTOR PULSE
// block … empty TOP GAINERS and TOP LOSERS lists." Never surface that to readers;
// fall back to the clean default headline/body instead.
const GARBAGE_INTEL = /sector pulse|top gainers|top losers|no (percentage|company description|headline|data)|not provided|provided in the|empty .{0,30}list/i
function firstParagraph(body) {
  if (!body) return null
  const line = body.split('\n').map((l) => l.trim())
    .find((l) => l && !/^#{1,3}\s/.test(l) && !/^[-*+]\s/.test(l) && !/^(?:---+|\*\*\*+)$/.test(l) && !/Atlas Intel/i.test(l))
  if (!line || GARBAGE_INTEL.test(line)) return null
  return line.replace(/[*_#]/g, '')
}
function Brief({ intel, movers = [], onInsights }) {
  const openTicker = useTicker()
  const [readerOpen, setReaderOpen] = useState(false)
  // Show the latest briefing that actually has content. The API filters
  // body=not.is.null, but an empty-STRING body still wins date.desc and would
  // otherwise strand the front page on the "posts after the cron" placeholder
  // (e.g. on a holiday/early-close day). Fall back to the most recent row that
  // yields a real paragraph; only truly-empty history shows the placeholder.
  const top = (intel || []).find((r) => firstParagraph(r?.body ?? r?.preview)) || intel?.[0]
  // atlas_intel.title is "Atlas Intel — {date}" — the date belongs in the
  // eyebrow, NOT the headline. The editorial lede + supporting lines come from
  // the body's first paragraph: headline = sentence 1, body = next 1-2.
  const dateLabel = (top?.title?.match(/[—–-]\s*(.+)$/)?.[1] || '').trim()
    || (top?.date ? new Date(top.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) : '')
  const para = firstParagraph(top?.body ?? top?.preview)
  // Split on sentence boundaries only — punctuation + space + capital/quote.
  // Avoids cutting decimals like "+17.1%" into "(+17." | "1%)".
  const sentences = para ? para.split(/(?<=[.!?])\s+(?=[A-Z“"])/).map((s) => s.trim()).filter(Boolean) : []
  const headline = sentences[0] || 'Atlas is reading today’s tape.'
  const body = sentences.slice(1, 3).join(' ')
    || 'Today’s briefing posts after the 5:40 PM CT cron. Atlas is tracking AI-infrastructure, defense-tech, and power megatrends.'
  const kicker = dateLabel ? `The Brief · ${dateLabel}` : 'The Brief'
  // The card shows a headline + 1-2 sentences; the FULL briefing (top.body) is
  // already loaded — tap to open the reader. Only offer it when there's a real
  // briefing (not the "posts after the cron" placeholder).
  const hasFull = !!para
  const openReader = () => { if (hasFull) setReaderOpen(true) }
  return (
    <Section>
      <SecHead kicker={kicker} action="This week →" onAction={onInsights} />
      <div onClick={openReader} role={hasFull ? 'button' : undefined} tabIndex={hasFull ? 0 : undefined}
        onKeyDown={hasFull ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openReader() } } : undefined}
        style={{ cursor: hasFull ? 'pointer' : 'default' }}>
        <h1 style={{ fontFamily: 'var(--studio-font-display)', fontWeight: 400, fontSize: 27, lineHeight: 1.16, letterSpacing: '-0.01em', color: 'hsl(var(--studio-text))', margin: '4px 0 7px', textWrap: 'pretty' }}>
          {headline}
        </h1>
        <p style={{ fontSize: 13.5, lineHeight: 1.55, color: 'hsl(var(--studio-text-muted))', margin: '0 0 10px', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {body}
        </p>
        {hasFull && (
          <div style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 10, letterSpacing: '0.1em', color: 'hsl(var(--studio-accent))', margin: movers.length ? '0 0 12px' : 0 }}>READ THE FULL BRIEFING →</div>
        )}
      </div>
      {readerOpen && <BriefSheet doc={top} dateLabel={dateLabel} onClose={() => setReaderOpen(false)} />}
      {movers.length > 0 && (
        // Biggest movers — a horizontally-scrollable row of content-sized chips
        // so the ticker never truncates ("W…"). Tap a chip → ticker drawer.
        <div className="atlas-scroll" style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 2 }}>
          {movers.map((m) => {
            const up = m.c >= 0
            return (
              <button key={m.t} onClick={() => openTicker(m.t)} style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 7, padding: '6px 11px', borderRadius: 999, cursor: 'pointer', background: 'hsl(var(--studio-surface) / 0.7)', border: '1px solid hsl(var(--studio-border) / 0.5)' }}>
                <StockLogo ticker={m.t} size={18} rounded="50%" />
                <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 12, color: 'hsl(var(--studio-text))', whiteSpace: 'nowrap' }}>{m.t}</span>
                <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 12, color: `hsl(var(${up ? '--studio-success' : '--studio-danger'}))`, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{up ? '+' : ''}{m.c.toFixed(1)}%</span>
              </button>
            )
          })}
        </div>
      )}
    </Section>
  )
}

/* ── 4. Wire teaser — swipeable news carousel (the "Glass" style from the v3
   handoff: docs/design/v3-handoff/v3/atlas-home.jsx). News-only since
   2026-07-22: editorial cards (article image + headline + 2-line blurb),
   depth falloff on the off-center cards, dots → full Wire. Signal cards
   moved off the front page — they live on The Wire. ── */
function wireRelTime(iso) {
  if (!iso) return ''
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}
function WireMiniCard({ item, name, dist, onOpen }) {
  const [hot, setHot] = useState(false)
  const [imgDead, setImgDead] = useState(false)
  const focused = dist === 0
  const scale = focused ? (hot ? 0.985 : 1) : 0.93
  return (
    <div onClick={onOpen}
      onPointerDown={() => setHot(true)} onPointerUp={() => setHot(false)} onPointerCancel={() => setHot(false)} onPointerLeave={() => setHot(false)}
      style={{ flexShrink: 0, width: 'calc(86% - 12px)', minWidth: 230, maxWidth: 300, scrollSnapAlign: 'center',
        position: 'relative', cursor: 'pointer', padding: '2px 2px',
        transform: `scale(${scale})`, opacity: focused ? 1 : 0.5,
        transition: 'transform 320ms cubic-bezier(0.2,0.8,0.2,1), opacity 320ms ease' }}>
      {item.image && !imgDead && (
        <img src={item.image} alt="" loading="lazy" onError={() => setImgDead(true)}
          style={{ width: '100%', height: 118, objectFit: 'cover', borderRadius: 10, marginBottom: 11,
            border: '1px solid hsl(var(--studio-border) / 0.5)', background: 'hsl(var(--studio-surface-2))', display: 'block' }} />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0, marginBottom: 10 }}>
        <StockLogo ticker={item.t} size={26} rounded="50%" />
        <div style={{ minWidth: 0 }}>
          <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 13, color: 'hsl(var(--studio-text))', letterSpacing: '0.02em' }}>{item.t}</span>
          {name && name !== item.t && (
            <div style={{ fontSize: 10.5, color: 'hsl(var(--studio-text-subtle))', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>{name}</div>
          )}
        </div>
      </div>
      <div style={{ fontFamily: 'var(--studio-font-display)', fontStyle: 'italic', fontSize: 15.5, lineHeight: 1.36, color: 'hsl(var(--studio-text))', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 42 }}>{item.headline}</div>
      {item.summary && (
        <div style={{ fontSize: 12, lineHeight: 1.45, color: 'hsl(var(--studio-text-subtle))', marginTop: 6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.summary}</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 11, paddingTop: 10, borderTop: '1px solid hsl(var(--studio-border) / 0.5)' }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'hsl(var(--studio-accent))' }} />
        <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 8.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {item.why || 'News'}{item.time ? ` · ${item.time}` : ''} · tap to read
        </span>
      </div>
    </div>
  )
}
function WireTeaser({ items, stocks, onSignals, openNews }) {
  const nameByTicker = useMemo(() => new Map((stocks || []).map((s) => [s.ticker, s.name])), [stocks])
  const trackRef = useRef(null)
  const [cur, setCur] = useState(0)
  // Show the full merged feed (newest-first signals + news) so you can keep
  // swiping — not just the first 6. Dots only make sense for a short list.
  const list = items || []
  const showDots = list.length > 1 && list.length <= 8
  function goTo(i) {
    const tr = trackRef.current; if (!tr) return
    const s = tr.children[i]; if (s) s.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    setCur(i)
  }
  function onScroll() {
    const tr = trackRef.current; if (!tr) return
    const base = tr.getBoundingClientRect().left + tr.clientWidth / 2
    let best = 0, md = Infinity
    ;[...tr.children].forEach((c, i) => { const r = c.getBoundingClientRect(); const d = Math.abs((r.left + r.width / 2) - base); if (d < md) { md = d; best = i } })
    setCur(best)
  }
  if (list.length === 0) return null
  return (
    <div style={{ marginBottom: 30 }}>
      <div style={{ padding: '0 20px' }}>
        <SecHead kicker="On the Wire" action="View all →" onAction={onSignals} />
      </div>
      <div ref={trackRef} onScroll={onScroll} className="atlas-scroll" style={{ display: 'flex', gap: 12, overflowX: 'auto', scrollSnapType: 'x mandatory', padding: '2px 20px 6px' }}>
        {list.map((item, i) => (
          <WireMiniCard key={item.id} item={item} name={nameByTicker.get(item.t)} dist={Math.abs(i - cur)}
            onOpen={() => openNews(signalToSheet(item))} />
        ))}
        <div style={{ flex: '0 0 8px' }} />
      </div>
      {showDots && (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 4 }}>
          {list.map((_, i) => (
            <button key={i} onClick={() => goTo(i)} aria-label={`Wire item ${i + 1}`} style={{ width: i === cur ? 16 : 6, height: 6, borderRadius: 999, padding: 0, border: 'none', cursor: 'pointer', background: i === cur ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-border))', transition: 'all 260ms var(--studio-ease)' }} />
          ))}
        </div>
      )}
    </div>
  )
}

/* ── 5. Pick of the Week ──────────────────────────────────── */
function PickOfWeek({ active, stats, history = [] }) {
  const openTicker = useTicker()
  const [pressed, setPressed] = useState(false)
  const [open, setOpen] = useState(false)
  const [toReceipts, setToReceipts] = useState(false) // open straight to the track record
  if (!active) return null
  const ticker = active.ticker
  const conv = active.conviction != null ? Math.round(active.conviction * (active.conviction <= 1 ? 100 : 1)) : null
  const thesis = active.thesis || active.headline || active.analysis_md?.split('\n').find((l) => l.trim()) || ''
  const sub = active.subhead || active.catalyst || null
  const press = { onPointerDown: () => setPressed(true), onPointerUp: () => setPressed(false), onPointerCancel: () => setPressed(false), onPointerLeave: () => setPressed(false) }
  // api/pick-of-week emits { count, avg_pct, hit_rate } — no wins/losses
  const picksCount = stats?.count, hitRate = stats?.hit_rate, avg = stats?.avg_pct ?? stats?.avgPct
  return (
    <Section>
      <SecHead kicker="Pick of the Week" />
      <div {...press} onClick={() => setOpen(true)} style={{ cursor: 'pointer', transform: pressed ? 'scale(0.985)' : 'scale(1)', transition: 'transform 140ms var(--studio-ease)' }}>
        <div className="atlas-trail">
          <div className="atlas-trail-inner" style={{ padding: '14px 16px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: 'hsl(var(--studio-accent))', animation: REDUCE ? 'none' : 'atlasLive 1.6s ease-in-out infinite' }} />
              <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 9.5, letterSpacing: '0.18em', color: 'hsl(var(--studio-accent))' }}>PICK OF THE WEEK · LIVE</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <StockLogo ticker={ticker} size={28} />
                <div>
                  <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 19, color: 'hsl(var(--studio-text))', letterSpacing: '0.02em' }}>{ticker}</span>
                  {active.company && <div style={{ fontSize: 11, color: 'hsl(var(--studio-text-subtle))', marginTop: 2 }}>{active.company}</div>}
                </div>
              </div>
              {conv != null && (
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5, fontFamily: 'var(--studio-font-mono)' }}>
                  <span style={{ fontSize: 8, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))' }}>CONV</span>
                  <span style={{ fontSize: 15, lineHeight: 1, color: 'hsl(var(--studio-text))', fontVariantNumeric: 'tabular-nums', letterSpacing: '0.01em' }}>{conv}</span>
                </span>
              )}
            </div>
            {thesis && <p style={{ fontFamily: 'var(--studio-font-display)', fontStyle: 'italic', fontSize: 15, lineHeight: 1.52, color: 'hsl(var(--studio-text))', margin: '0 0 7px', textWrap: 'pretty', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{thesis}</p>}
            {sub && (
              <div style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 10, letterSpacing: '0.04em', color: 'hsl(var(--studio-text))', display: 'flex', alignItems: 'center', gap: 5 }}>
                <span>{sub}</span><span style={{ opacity: 0.55 }}>→</span>
              </div>
            )}
            {(picksCount > 0 || avg != null) && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, paddingTop: 11, borderTop: '1px solid hsl(var(--studio-border) / 0.5)' }}>
                <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 9.5, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))' }}>
                  {picksCount > 0 && <>VERIFIED RECORD <span style={{ color: 'hsl(var(--studio-text))' }}>{picksCount} PICKS{hitRate != null && <> · <span style={{ color: `hsl(var(${hitRate >= 0.5 ? '--studio-success' : '--studio-danger'}))` }}>{Math.round(hitRate * 100)}% HIT</span></>}</span></>}
                  {avg != null && <> · AVG <span style={{ color: `hsl(var(${avg >= 0 ? '--studio-success' : '--studio-danger'}))` }}>{avg >= 0 ? '+' : ''}{Number(avg).toFixed(1)}%</span></>}
                </span>
                {history.length > 0 && (
                  <span
                    onClick={(e) => { e.stopPropagation(); setToReceipts(true); setOpen(true) }}
                    style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 9.5, letterSpacing: '0.08em', color: 'hsl(var(--studio-accent-soft))', cursor: 'pointer', padding: '2px 0' }}>RECEIPTS ({history.length}) →</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {open && <PickSheet active={active} stats={stats} history={history} initialReceipts={toReceipts} onClose={() => { setOpen(false); setToReceipts(false) }} openTicker={openTicker} />}
    </Section>
  )
}

/* ── compose ──────────────────────────────────────────────── */
function OverviewBody() {
  const cfg = DEFAULT_CFG
  const { openTicker } = useStockDetail()
  const { intel } = useAtlasIntel()
  const { active: pick, stats, history: pickHistory } = usePickOfWeek()
  const { news } = useNews()
  const [newsItem, setNewsItem] = useState(null) // open NewsSheet (news cards)
  // On the Wire = news only, newest first, since 2026-07-22 — the signal cards
  // (OPTIONS badges, CONV numbers, bullish/bearish flow) read as intimidating
  // on the front page. Signals still live one tap away on The Wire page, where
  // the vocabulary is expected. News cards open the in-app NewsSheet.
  const wireItems = useMemo(() => {
    // Dedup news by URL — fetch-news writes one news_articles row per
    // (ticker,url), so a market-wide article tagged to N names arrives as N
    // identical rows. Collapse to one card per article (mirrors Wire.jsx),
    // else the teaser shows the same story over and over.
    const seenUrl = new Set()
    const art = []
    for (const a of (news || [])) {
      if (a.url && seenUrl.has(a.url)) continue
      if (a.url) seenUrl.add(a.url)
      art.push({
        kind: 'news',
        id: `news-${a.id}`,
        t: a.ticker,
        type: 'news',
        dir: null,
        conv: null,
        headline: a.headline,
        summary: a.summary || null, // NewsSheet body
        why: a.source || null,      // publisher (NewsSheet reads `why`; card footer too)
        url: a.url || null,
        image: a.image_url || null,
        ts: a.published_at,
        time: wireRelTime(a.published_at),
      })
    }
    const tsMs = (x) => new Date(x.ts || 0).getTime()
    return art.filter((x) => x.t && x.headline).sort((a, b) => tsMs(b) - tsMs(a)).slice(0, 24)
  }, [news])

  // Live universe price/% (same source Explore uses) → hero-star movers (A2)
  // + the Markets-door sector marquee (A3).
  const { stocks } = useAtlasStocks()
  // Full browseable universe (incl. hidden gems) — only for the Explore-door
  // count, so the door's "+N NAMES" matches what Explore actually shows when
  // tapped. Movers/sector perf below stay on the active set.
  const { stocks: allStocks } = useAtlasStocks({ includeInactive: true })
  const tickers = useMemo(() => (stocks || []).map((s) => s.ticker).filter(Boolean), [stocks])
  const { prices } = useLivePrices(tickers)
  // Live whole-index move on the day = equal-weight avg of change1d across the
  // active (main-tier) universe — the intraday equivalent of the atlas_index
  // close. Drives the Atlas Index 1D headline in Pulse. null until prices land.
  const liveIndexPct = useMemo(() => {
    let sum = 0, n = 0
    for (const s of (stocks || [])) {
      const c = prices[s.ticker]?.change1d
      if (typeof c === 'number') { sum += c; n++ }
    }
    return n ? sum / n : null
  }, [stocks, prices])
  // Hero Pulse: the Atlas Index source shows the LIVE whole-index % on 1D
  // (liveIndexPct) and the daily atlas_index series (via idxLevels below) on
  // longer timeframes; the ~40-name basket is the chart-bars composite for the
  // OTHER sources + the live 1D line shape. Slice keeps it under the 50-cap.
  const basket = useMemo(() => tickers.slice(0, 40), [tickers])
  // Sector → tickers, so the hero source toggle can chart any sector composite.
  const sectorMap = useMemo(() => {
    const m = {}
    for (const s of (stocks || [])) {
      if (!s.sector || !s.ticker) continue
      ;(m[s.sector] = m[s.sector] || []).push(s.ticker)
    }
    return m
  }, [stocks])
  const { levels: idxLevels } = useAtlasIndex()
  // Hero chart source list: Atlas Index · Portfolio · Watchlists · Sectors.
  const { tickers: portfolioTickers, signedIn } = usePortfolioTickers()
  const { watchlists } = useWatchlists()
  const chartSources = useMemo(() => {
    const out = [{ key: 'atlas', label: 'Atlas Index', tickers: basket, group: 'index' }]
    // Overnight world indices — Tokyo/Seoul/Taipei trade while the US sleeps
    // (the upstream tape for the AI supply chain). Single-symbol "baskets"
    // through the same composite path; chart-bars maps these keys to Yahoo
    // index symbols (^N225/^KS11/^TWII).
    out.push({ key: 'idx:NIKKEI', label: 'Nikkei 225 · Tokyo', tickers: ['NIKKEI'], group: 'world' })
    out.push({ key: 'idx:KOSPI', label: 'KOSPI · Seoul', tickers: ['KOSPI'], group: 'world' })
    out.push({ key: 'idx:TAIEX', label: 'TAIEX · Taipei', tickers: ['TAIEX'], group: 'world' })
    if (signedIn && portfolioTickers.length) out.push({ key: 'portfolio', label: 'Portfolio', tickers: portfolioTickers, group: 'portfolio' })
    for (const w of (watchlists || [])) if (w.tickers && w.tickers.length) out.push({ key: 'wl:' + w.id, label: w.name, tickers: w.tickers, group: 'watchlist' })
    for (const name of Object.keys(sectorMap).sort()) out.push({ key: 'sec:' + name, label: name, tickers: sectorMap[name], group: 'sector' })
    return out
  }, [basket, signedIn, portfolioTickers, watchlists, sectorMap])
  // Biggest movers (live |1D %|, both directions) → the scrollable mover chips
  // under The Brief.
  const topMovers = useMemo(() => {
    const rows = (stocks || [])
      .map((s) => ({ t: s.ticker, c: prices[s.ticker]?.change1d }))
      .filter((r) => r.t && typeof r.c === 'number')
    rows.sort((a, b) => Math.abs(b.c) - Math.abs(a.c))
    return rows.slice(0, 6)
  }, [stocks, prices])
  // The rotating logo cluster cycles the WHOLE active universe (not just
  // movers), market-cap desc so logo-rich large caps lead and the no-logo
  // long tail gets filtered by LOGO_MISSING as it's reached.
  const doorPool = useMemo(() => {
    const rows = (stocks || []).filter((s) => s.ticker)
    rows.sort((a, b) => (Number(b.market_cap_usd_m) || 0) - (Number(a.market_cap_usd_m) || 0))
    return rows.map((s) => s.ticker)
  }, [stocks])

  // Scroll container ref. (Pull-to-refresh was removed: it hijacked downward
  // drags and kept popping up over the hero chart. The Observatory starfield +
  // its measurer were DELETED 2026-08-15 after 2 months parked — resurrect
  // from git pre-#847 if the floating-logos hero is ever wanted again.)
  const scrollRef = useRef(null)


  return (
    <CfgCtx.Provider value={cfg}>
      <TickerCtx.Provider value={openTicker}>
        <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', background: 'radial-gradient(140% 75% at 50% -10%, hsl(var(--studio-background)) 0%, hsl(var(--studio-overlay)) 100%)' }}>
          <div ref={scrollRef} className="atlas-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingTop: 16, position: 'relative' }}>
            <div style={{ position: 'relative', zIndex: 1 }}>
              <Header />
              <Pulse sources={chartSources} idxLevels={idxLevels} liveIndexPct={liveIndexPct} />
              <MarketsDoor onOpen={() => v2Navigate('/markets')} pool={doorPool} />
              <Brief intel={intel} movers={topMovers} onInsights={() => v2Navigate('/insights')} />
              <WireTeaser items={wireItems} stocks={stocks} onSignals={() => v2Navigate('/signals')} openNews={setNewsItem} />
              <PickOfWeek active={pick} stats={stats} history={pickHistory} />
              <div style={{ height: 8 }} />
            </div>
          </div>
          <div style={{ flexShrink: 0, borderTop: '1px solid hsl(var(--studio-border) / 0.5)', background: 'hsl(var(--studio-background) / 0.72)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
            <TickerStrip />
          </div>
          <BottomNav />
          {newsItem && <NewsSheet item={newsItem} onClose={() => setNewsItem(null)} onTicker={openTicker} />}
        </div>
      </TickerCtx.Provider>
    </CfgCtx.Provider>
  )
}

export function Overview() {
  return (
    <div className="v3-root" style={{ height: 'var(--v3-vh, 100dvh)', display: 'flex', flexDirection: 'column' }}>
      <StockDetailProvider>
        <OverviewBody />
      </StockDetailProvider>
    </div>
  )
}
